import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';

/**
 * For requests made with `responseType: 'blob'`, axios also delivers error
 * bodies as a Blob, which would surface as an unreadable "[object Blob]".
 * Read it back so callers get the ProblemDetail JSON like any other request.
 */
async function readErrorResponseData(data: unknown): Promise<unknown> {
    if (!(data instanceof Blob)) {
        return data;
    }

    try {
        const text = await data.text();
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    } catch {
        return data;
    }
}

/**
 * Isomorphic httpInstance
 * @param baseUrl
 */
export const httpInstance = <T>(baseUrl: string, options: AxiosRequestConfig) => {
    const requestOptions: AxiosRequestConfig = {
        baseURL: baseUrl,
        headers: {
            Authorization: `Bearer ${Cookies.get('CF_Authorization')}`, // Add the cookie value
            'Content-Type': 'application/json',
        },
        ...options,
    };

    const instance: AxiosInstance = axios.create(requestOptions);

    instance.interceptors.request.use(
        (request) => request,
        (requestError) => Promise.reject(requestError),
    );

    instance.interceptors.response.use(
        (response: AxiosResponse<T>) => response,
        async (responseError: AxiosError<T>) => {
            // A request the caller aborted -- a page navigation, a filter change, an unmount --
            // is not a failure. Logging it would put a console entry (and a Sentry breadcrumb) in
            // front of whoever is debugging a real one.
            if (axios.isCancel(responseError)) {
                return Promise.reject(responseError);
            }

            console.warn('Admi response error', responseError);

            if (responseError.response?.status !== undefined && [401, 403].includes(responseError.response?.status)) {
                return Promise.reject(responseError);
            } else {
                if (responseError.response) {
                    return Promise.reject(await readErrorResponseData(responseError.response.data));
                } else {
                    return Promise.reject(responseError);
                }
            }
        },
    );

    return instance;
};
